import {
  Checkbox,
  FormControl,
  FormControlProps,
  FormGroup,
  FormHelperTextProps,
  FormLabelProps,
} from "@mui/material";
import { getAriaDescribedByValue } from "core/model/utils/strings";
import { Chip, ChipProps } from "ds_legacy/components/Chip";
import {
  margin as computeMargin,
  dp,
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import React, { CSSProperties, ReactNode, useState } from "react";
import {
  FieldValidation,
  FormElement,
  FormElementProps,
  FormWatcher,
  isValid,
} from "react-forms-state";
import styled from "styled-components";
import { SubheadingFormLegend } from "../FormComponents/SubheadingFormLegend";
import Tooltip from "../Tooltip";
import { Validation } from "../Validation";

export type ConnectedChipGroupProps = ChipGroupPresenterProps &
  FormElementProps;

export type ChipGroupPresenterProps = {
  ariaDescribedBy?: string;
  chipStyle?: {
    bold?: boolean;
    height?: CSSProperties["height"];
    justifyContent?: CSSProperties["justifyContent"];
    width?: CSSProperties["width"];
  };
  className?: string;
  disabled?: boolean;
  elementName?: string;
  formControlSx?: FormControlProps["sx"];
  formGroupSx?: CSSProperties;
  formHelperTextSx?: FormHelperTextProps["sx"];
  formLabelSx?: FormLabelProps["sx"];
  onChange: (arg: ToType) => void;
  options: Array<ChipOption>;
  required?: boolean;
  row?: boolean;
  sortOrder?: Record<number, number>;
  squared?: boolean;
  title?: string;
  validation?: FieldValidation;
  value: Array<number> | undefined;
};

export const ChipGroupContainer = styled.div<{
  margin?: CSSProperties["margin"];
}>`
  display: flex;
  justify-content: flex-start;
  flex: 1 1 auto;
  flex-wrap: wrap;
  margin: ${(props) => props.margin || computeMargin(0, 0, 0, 2)};
`;

export function setMultipleValues(
  values: Array<ToType> | ToType,
  selectedValue: ToType,
) {
  if (!values || values.length === 0) return [selectedValue];

  const copy = [...values];
  if (copy && copy.length > 0) {
    if (!copy.includes(selectedValue)) {
      copy.push(selectedValue);
      return copy;
    } else if (copy.includes(selectedValue)) {
      copy.splice(values.indexOf(selectedValue), 1);
      return copy.length > 0 ? copy : null;
    }
  }

  return null;
}

export function getOrderedItems<T extends { value: number }>(
  sortOrder: Record<number, number> | undefined,
  options: T[],
): T[] {
  if (!sortOrder) return options;

  return [...options].sort((a, b) => {
    const orderA = sortOrder[a.value];
    const orderB = sortOrder[b.value];
    
    if (orderA === undefined && orderB === undefined) return 0;
    if (orderA === undefined) return 1;
    if (orderB === undefined) return -1;

    return orderA - orderB;
  });
}

export type ChipOption = {
  disabled?: boolean;
  icon?: ReactNode;
  iconPosition?: "start" | "end";
  name: string;
  onClick?: () => void;
  tooltipText?: string;
  value: ToType;
};

function getId(elementName: string | undefined, name: string) {
  return `chip-${elementName ? `${elementName}-` : ""}${name
    .replace(/[ :]/g, "")
    .replace("<", "below")}`;
}

const ChipWithIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${dp(4)};
`;

const IconContainer = styled.div`
  cursor: pointer;
  display: inline-flex;
  margin-left: dp(4);
  padding: dp(4);
  border-radius: dp(4);
  z-index: 10;
  position: relative;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const StyledCheckbox = styled(Checkbox)<{ width?: CSSProperties["width"] }>`
  margin: ${margin(0)};
  border-radius: 1;
  width: ${({ width }) => width ?? undefined};

  // needs to be set manually here as MuiCheckbox got a styleOverrides on materials/theme.tsx
  &.MuiCheckbox-root {
    padding: ${padding(0)};
    // required for accessiblity to ensure focus outline is visible
    outline-offset: ${important(dp(1))};
  }

  &:hover {
    background-color: "transparent";
  }
`;

const HoverCheckbox = ({
  ariaDescribedBy,
  chipProps,
  elementName,
  errorTextId,
  id,
  onChange,
  option,
  validation,
  value,
}: {
  ariaDescribedBy?: string;
  chipProps: ChipProps;
  elementName: ChipGroupPresenterProps["elementName"];
  errorTextId: string;
  id: string;
  onChange: ChipGroupPresenterProps["onChange"];
  option: ChipOption;
  validation: ChipGroupPresenterProps["validation"];
  value: ChipGroupPresenterProps["value"];
}) => {
  const [hovered, setHovered] = useState(false);

  const handleIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (option.onClick) {
      option.onClick();
    }
  };

  const chipContent = (
    <ChipWithIconContainer>
      {option.icon && option.iconPosition === "start" ? (
        <>
          <IconContainer onClick={handleIconClick}>{option.icon}</IconContainer>
          {option.name}
        </>
      ) : option.icon ? (
        <>
          {option.name}
          <IconContainer onClick={handleIconClick}>{option.icon}</IconContainer>
        </>
      ) : (
        option.name
      )}
    </ChipWithIconContainer>
  );

  return (
    <StyledCheckbox
      checked={chipProps.selected}
      checkedIcon={
        <Chip {...chipProps} hovered={hovered}>
          {chipContent}
        </Chip>
      }
      disabled={chipProps.disabled}
      disableTouchRipple
      focusRipple
      icon={
        <Chip {...chipProps} hovered={hovered}>
          {chipContent}
        </Chip>
      }
      id={id}
      inputProps={{
        ...{ ["data-testid"]: id },
        "aria-describedby": getAriaDescribedByValue([
          !isValid(validation) && errorTextId,
          ariaDescribedBy,
        ]),
        "aria-invalid": !isValid(validation) ? true : undefined,
      }}
      name={elementName}
      required={chipProps.required}
      value={option.value}
      width={chipProps?.width}
      onChange={() => {
        if (chipProps.disabled) {
          return;
        }
        onChange(setMultipleValues(value, option.value));
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    />
  );
};

export function ChipGroupPresenter({
  ariaDescribedBy,
  chipStyle,
  elementName,
  formControlSx,
  formGroupSx,
  formHelperTextSx,
  formLabelSx,
  onChange,
  options,
  required,
  row,
  sortOrder,
  squared,
  title,
  validation,
  value,
}: ChipGroupPresenterProps) {
  const errorTextId = elementName ? `${elementName}-error-text` : "error-text";
  const hasError = isValid(validation) === false;
  const sortedOptions = getOrderedItems(sortOrder, options);

  const getChip = (option: ChipOption) => {
    const isDisabled = option.disabled;
    const isSelected =
      value && Array.isArray(value) && value.length > 0
        ? value.includes(option.value)
        : option.value === value;
    const id = getId(elementName, option.name);

    const chipProps: ChipProps = {
      bold: isSelected,
      className: !isDisabled && isSelected ? "checked" : undefined,
      disabled: isDisabled,
      error: hasError,
      opacity: isDisabled && isSelected ? 0.5 : undefined,
      primary: isSelected,
      required,
      selected: isSelected,
      squared,
      ...chipStyle,
    };

    return (
      <label key={`chip-${option.name}`} htmlFor={id}>
        <Tooltip title={option.tooltipText}>
          <HoverCheckbox
            ariaDescribedBy={ariaDescribedBy}
            chipProps={chipProps}
            elementName={elementName}
            errorTextId={errorTextId}
            id={id}
            option={option}
            validation={validation}
            value={value}
            onChange={onChange}
          />
        </Tooltip>
      </label>
    );
  };

  return (
    <FormWatcher watchPath={elementName ?? ""}>
      {({ watchedValidation }) => {
        const valid = isValid(watchedValidation);
        return (
          <FormControl
            component="fieldset"
            error={!valid}
            sx={{ ...formControlSx }}
          >
            <SubheadingFormLegend
              error={hasError}
              required={required}
              sx={{ padding: "0", ...formLabelSx }}
            >
              {title}
            </SubheadingFormLegend>
            <FormGroup
              row={row}
              sx={{
                gap: sizing(1),
                margin: computeMargin(1, 0, 0, 2),
                ...formGroupSx,
              }}
            >
              {sortedOptions.map(getChip)}
            </FormGroup>
            <Validation
              elementName={elementName ?? "chip-group"}
              formHelperTextSx={formHelperTextSx}
              hasCustomValidation
              id={errorTextId}
              validation={validation}
            />
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}

const ConnectedChipGroup =
  FormElement()<ConnectedChipGroupProps>(ChipGroupPresenter);
export default ConnectedChipGroup;
